import { useMutation, useQuery } from '@tanstack/react-query'
import { api, type AuditRow, type ChainHead } from '../api'
import { useAuth } from '../auth'
import { Card, ErrorNote, Loading, Note, Stat } from '../components/ui'

interface VerifyResult {
  ok: boolean
  checked: number
  from_seq: number | null
  to_seq: number | null
  head_hash: string | null
  broken_at_seq: number | null
  problem: string | null
}

function reasonOf(detail: Record<string, unknown>): string | null {
  const reason = detail?.reason
  return typeof reason === 'string' ? reason : null
}

function changeSummary(detail: Record<string, unknown>): string | null {
  const changes = detail?.changes as Record<string, { from?: unknown; to?: unknown }> | undefined
  if (!changes) return null
  return Object.entries(changes)
    .slice(0, 3)
    .map(([field, move]) =>
      typeof move === 'object' && move !== null && 'to' in move
        ? `${field}: ${JSON.stringify(move.from)} → ${JSON.stringify(move.to)}`
        : `${field}: ${JSON.stringify(move)}`,
    )
    .join('  ·  ')
}

export default function AuditLog() {
  const { can } = useAuth()

  const rows = useQuery({
    queryKey: ['audit', 'log'],
    queryFn: () => api.get<AuditRow[]>('/audit/log?limit=100'),
  })
  const head = useQuery({
    queryKey: ['audit', 'chain'],
    queryFn: () => api.get<ChainHead>('/audit/chain'),
    enabled: can('audit.chain.verify'),
  })
  const verify = useMutation({
    mutationFn: () => api.post<VerifyResult>('/audit/verify'),
  })

  if (rows.isLoading) return <Loading what="Loading the audit trail" />

  return (
    <>
      <ErrorNote error={rows.error} />

      {can('audit.chain.verify') && (
        <>
          <div className="grid">
            <Stat label="Entries" value={head.data?.head_seq ?? '—'} note="append-only" />
            <Stat
              label="Since last off-box copy"
              value={head.data?.rows_since_anchor ?? '—'}
              note={
                head.data?.anchored_at
                  ? `anchored ${head.data.anchored_at.slice(0, 10)}`
                  : 'never anchored'
              }
            />
            <div className="card stat">
              <div className="label">Chain integrity</div>
              <div className="value">
                {verify.data ? (verify.data.ok ? 'Verified' : 'Broken') : '—'}
              </div>
              <div className="note">
                <button
                  className="small"
                  onClick={() => verify.mutate()}
                  disabled={verify.isPending}
                >
                  {verify.isPending ? 'Checking…' : 'Verify now'}
                </button>
              </div>
            </div>
          </div>

          {verify.data &&
            (verify.data.ok ? (
              <Note kind="ok">
                Checked {verify.data.checked} entries from {verify.data.from_seq} to{' '}
                {verify.data.to_seq}. Every row still hashes to what it claims, and each
                follows the one before it.
              </Note>
            ) : (
              <Note kind="error">
                The chain breaks at entry {verify.data.broken_at_seq}: {verify.data.problem}.
                Everything before it still verifies.
              </Note>
            ))}
          <ErrorNote error={verify.error} />
        </>
      )}

      <Card
        title="Recent activity"
        hint="newest first — nothing here can be edited or deleted, by anyone"
        tight
      >
        <table>
          <thead>
            <tr>
              <th className="num">#</th>
              <th>When</th>
              <th>Who</th>
              <th>Action</th>
              <th>Object</th>
              <th>Detail</th>
            </tr>
          </thead>
          <tbody>
            {(rows.data ?? []).map((r) => {
              const reason = reasonOf(r.detail)
              const changes = changeSummary(r.detail)
              return (
                <tr key={r.seq}>
                  <td className="num faint">{r.seq}</td>
                  <td className="muted">{r.at.replace('T', ' ').slice(0, 19)}</td>
                  <td className="muted">{r.actor_role ?? <span className="faint">—</span>}</td>
                  <td>{r.action.replace(/_/g, ' ')}</td>
                  <td className="mono muted">{r.object_type}</td>
                  <td className="muted" style={{ maxWidth: 380 }}>
                    {reason && (
                      <div>
                        <strong>Reason:</strong> {reason}
                      </div>
                    )}
                    {changes && <div className="mono faint">{changes}</div>}
                    {!reason && !changes && <span className="faint">—</span>}
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>
      </Card>
    </>
  )
}
